import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserGroupAuditLog } from "@/services/api/users/usersGraphqlService";
import { WorkflowAuditLog, ProgramStatusAuditLog } from "@/services/features/WorkflowService";
import { PatientGroupAuditLog } from "@/services/features/GroupsService";
import { ActivityAuditLog } from "@/services/features/ActivityService";
import { UserAuditLog } from "@/services/features/UserDataService";
import { FacilityAvailabilityAuditLog } from "@/hooks/facility-availability/useFacilityAvailabilityAuditLog";
import { formatDate } from "@/utils/dateUtils";
import LoaderComponent from "@/components/common/LoaderComponent";
import { Badge } from "@/components/ui/badge";

interface HistoryEntry {
  id: string;
  type: "added" | "removed" | "updated" | "created";
  patientCount: number;
  timestamp: string;
  user: string;
  clinic?: string;
  provider?: string;
  note?: string;
  message?: string;
  actionType?: string;
}

interface OperationsHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityName: string;
  entityType: string;
  historyEntries?: HistoryEntry[];
  auditLogs?: UserGroupAuditLog[] | WorkflowAuditLog[] | PatientGroupAuditLog[] | ActivityAuditLog[] | UserAuditLog[] | ProgramStatusAuditLog[] | FacilityAvailabilityAuditLog[];
  loading?: boolean;
}

// Mock history data
const mockHistoryEntries: HistoryEntry[] = [
  {
    id: "1",
    type: "added",
    patientCount: 10,
    timestamp: "12:04 pm, Jan 08, 2025",
    user: "Lijo",
    clinic: "Gilbert Clinic",
    provider: "Mark Zainea",
    note: "Few patients became inactive so added new."
  },
  {
    id: "2", 
    type: "removed",
    patientCount: 10,
    timestamp: "11:35 am, Jan 06, 2025",
    user: "Lijo",
    clinic: "Gilbert Clinic", 
    provider: "Mark Zainea",
    note: "Moved spanish speaking patients from this group."
  },
  {
    id: "3",
    type: "added", 
    patientCount: 35,
    timestamp: "04:15 pm, Jan 05, 2025",
    user: "Lijo",
    clinic: "Cardiology Associates of Michigan",
    provider: "Tamam Mohammad",
    note: "Added new patients to balance inactive patients"
  },
  {
    id: "4",
    type: "added",
    patientCount: 55, 
    timestamp: "05:23 pm, Jan 04, 2025",
    user: "Lijo",
    clinic: "Cardiology Associates of Michigan",
    provider: "Tamam Mohammad"
  }
];

export const OperationsHistoryModal: React.FC<OperationsHistoryModalProps> = ({
  open,
  onOpenChange,
  entityName,
  entityType,
  historyEntries = mockHistoryEntries,
  auditLogs,
  loading = false,
}) => {
  const formatFiltersText = (entry: HistoryEntry) => {
    const filters = [];
    if (entry.clinic) filters.push(`${entry.clinic} (Clinic)`);
    if (entry.provider) filters.push(`${entry.provider} (Provider)`);
    return filters.join(", ");
  };

  // Parse metadata to extract operation details
  const parseMetadata = (log: UserGroupAuditLog | WorkflowAuditLog) => {
    try {
      const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
      
      // Use the message from metadata if available
      const message = metadata?.message || log.action_type;
      
      return { message };
    } catch (error) {
      console.error('Error parsing metadata:', error);
      return { message: log.action_type };
    }
  };

  // Check if it's a UserGroupAuditLog
  const isUserGroupAuditLog = (log: any): log is UserGroupAuditLog => {
    return 'pep_user_group_audit_log_id' in log && 'user_data' in log;
  };

  // Check if it's a PatientGroupAuditLog
  const isPatientGroupAuditLog = (log: any): log is PatientGroupAuditLog => {
    return 'pep_patient_group_audit_log_id' in log;
  };

  // Check if it's an ActivityAuditLog
  const isActivityAuditLog = (log: any): log is ActivityAuditLog => {
    return 'pep_activity_audit_log_id' in log;
  };

  // Check if it's a UserAuditLog
  const isUserAuditLog = (log: any): log is UserAuditLog => {
    return 'action' in log && 'changed_at' in log && 'changed_by' in log && !('action_type' in log);
  };

  // Check if it's a ProgramStatusAuditLog
  const isProgramStatusAuditLog = (log: any): log is ProgramStatusAuditLog => {
    return 'entity_id' in log && 'request_id' in log && 'metadata' in log;
  };

  // Check if it's a FacilityAvailabilityAuditLog
  const isFacilityAvailabilityAuditLog = (log: any): log is FacilityAvailabilityAuditLog => {
    return 'facility_availability_id' in log && 'availability_old' in log && 'availability_new' in log;
  };

  // Use audit logs if provided, otherwise fall back to historyEntries
  const displayEntries = auditLogs ? auditLogs.map(log => {
    let userName: string | undefined;
    let logId: string | undefined;
    let action: string;
    let timestamp: string;
    
    if (isUserGroupAuditLog(log)) {
      // Handle UserGroupAuditLog
      const userData = typeof log.user_data === 'string' ? JSON.parse(log.user_data) : log.user_data;
      userName = userData?.first_name && userData?.last_name 
        ? `${userData.first_name} ${userData.last_name}` 
        : undefined;
      logId = log.pep_user_group_audit_log_id.toString();
      action = log.action_type;
      timestamp = log.action_datetime;
    } else if (isPatientGroupAuditLog(log)) {
      // Handle PatientGroupAuditLog
      logId = log.pep_patient_group_audit_log_id.toString();
      action = log.action_type;
      timestamp = log.action_datetime;
      // PatientGroupAuditLog doesn't have user_data, so we leave userName undefined
    } else if (isActivityAuditLog(log)) {
      // Handle ActivityAuditLog
      logId = log.pep_activity_audit_log_id.toString();
      action = log.action_type;
      timestamp = log.action_datetime;
      // ActivityAuditLog doesn't have user_data, so we leave userName undefined
    } else if (isUserAuditLog(log)) {
      // Handle UserAuditLog
      logId = undefined;
      action = log.action;
      timestamp = log.changed_at;
      userName = log.changed_by || undefined;
    } else if (isProgramStatusAuditLog(log)) {
      // Handle ProgramStatusAuditLog
      logId = log.request_id;
      
      // Parse metadata to extract action and timestamp
      const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
      
      // Extract action type from action_description if available
      // Example: "CREATE operation on WORKFLOW_STATUS" -> "CREATE"
      let extractedAction = 'UPDATE';
      if (metadata?.action_description) {
        const actionMatch = metadata.action_description.match(/^(CREATE|UPDATE|DELETE)/i);
        if (actionMatch) {
          extractedAction = actionMatch[1].toUpperCase();
        }
      } else if (metadata?.action) {
        extractedAction = metadata.action;
      }
      
      action = extractedAction;
      timestamp = metadata?.timestamp || new Date().toISOString();
      userName = metadata?.user || undefined;
    } else if (isFacilityAvailabilityAuditLog(log)) {
      // Handle FacilityAvailabilityAuditLog
      logId = `${log.facility_availability_id}-${log.action_datetime}`;
      action = log.action_type;
      timestamp = log.action_datetime;
      userName = log.action_by ? `User ${log.action_by}` : undefined;
    } else {
      // Handle WorkflowAuditLog
      const workflowLog = log as WorkflowAuditLog;
      logId = workflowLog.pep_workflow_audit_log_id.toString();
      action = workflowLog.action_type;
      timestamp = workflowLog.action_datetime;
      // WorkflowAuditLog doesn't have user_data, so we leave userName undefined
    }
    
    // Get message - for ProgramStatusAuditLog and UserAuditLog use action_description from metadata
    let message: string;
    if (isProgramStatusAuditLog(log) || isUserAuditLog(log)) {
      const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
      message = metadata?.action_description || action;
    } else if (isFacilityAvailabilityAuditLog(log)) {
      // Build message from facility availability audit log data
      const apiMetadata = typeof log.api_metadata === 'string' ? JSON.parse(log.api_metadata) : log.api_metadata;
      message = apiMetadata?.message || `${action} operation on Facility Availability`;
    } else {
      message = parseMetadata(log as any).message;
    }
    
    return {
      id: logId || `${action}-${timestamp}`,
      type: action.toLowerCase() as 'added' | 'removed' | 'updated' | 'created',
      patientCount: 0,
      timestamp: formatDate(timestamp),
      user: userName,
      note: isUserGroupAuditLog(log) ? log.notes || undefined : undefined,
      message,
      actionType: action,
    };
  }) : historyEntries;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="operations_history_dialog" className="max-w-2xl max-h-[80vh] p-0 bg-background border">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-0">
          <DialogTitle id="operations_history_title" className="text-lg font-semibold text-foreground">
            {entityType} Operation History
          </DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pb-6 pt-4">
          <div className="border-t pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <LoaderComponent />
              </div>
            ) : displayEntries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No history found
              </div>
            ) : (
              <div className="space-y-6 max-h-[50vh] overflow-y-auto">
                {displayEntries.map((entry, index) => (
                <div key={entry.id}>
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex items-start gap-4 flex-1">
                      {entry.actionType && (
                        <Badge 
                          variant="outline" 
                          className={`mt-1 flex-shrink-0 ${
                            entry.actionType === 'CREATE' 
                              ? 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' 
                              : entry.actionType === 'UPDATE'
                              ? 'bg-green-500/10 text-green-600 border-green-500/20'
                              : 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                          }`}
                        >
                          {entry.actionType}
                        </Badge>
                      )}
                      <div className="space-y-2 flex-1">
                        <div className="font-medium text-foreground text-base">
                          {entry.message || (entry.actionType ? `${entry.actionType} operation completed` : `${entry.patientCount} patients ${entry.type}.`)}
                        </div>
                        {(entry.clinic || entry.provider) && (
                          <div className="text-sm text-muted-foreground">
                            <span className="font-medium">Filters Applied</span> - {formatFiltersText(entry)}
                          </div>
                        )}
                        {entry.note && (
                          <div className="text-sm italic text-muted-foreground">
                            <span className="font-medium">Note:</span> {entry.note}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground flex-shrink-0 min-w-[140px]">
                      <div className="text-muted-foreground">{entry.timestamp}</div>
                    </div>
                  </div>
                  {index < displayEntries.length - 1 && (
                    <hr className="my-6 border-border" />
                  )}
                </div>
              ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};